import React, { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";

// GTM tracking utility
declare global {
  interface Window {
    dataLayer: any[];
  }
}

const PageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    let esc = "";
    let csc = "";

    try {
      const connectionData = localStorage.getItem("connectionData");
      if (connectionData) {
        const parsed = JSON.parse(connectionData);
        esc = parsed.esc || parsed.eventXLShowCode || "";
        csc = parsed.csc || parsed.cdsShowCode || parsed.cdsEventCode || "";
      }
    } catch (error) {
      console.error("Error parsing connection data for show codes:", error);
    }

    // Fallback for first load before connectionData is available
    if (!esc) {
      esc = (searchParams.get("esc") || "").trim();
    }
    if (!csc) {
      csc = (searchParams.get("csc") || "").trim();
    }

    // Track page views with GTM including showCode
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "page_view",
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname,
        url: location.pathname + location.search, // Fragment showing the route
        esc: esc.toUpperCase(),
        csc: csc.toUpperCase(),
      });
    }
  }, [location, searchParams]);

  useEffect(() => {
    // Add click tracking to all buttons
    const handleButtonClick = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === "BUTTON" || target.closest("button")) {
        const button =
          target.tagName === "BUTTON" ? target : target.closest("button");
        const buttonText = button?.textContent?.trim() || "Unknown Button";
        const buttonClass = button?.className || "";

        trackEvent("button_click", {
          button_text: buttonText,
          button_class: buttonClass,
          page_path: location.pathname,
        });
      }
    };

    // Add input focus tracking
    const handleInputFocus = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
        const inputType = target.type || target.tagName.toLowerCase();
        const inputName = target.name || target.id || "unnamed_input";
        const placeholder = target.placeholder || "";

        trackEvent("input_focus", {
          input_type: inputType,
          input_name: inputName,
          placeholder: placeholder,
          page_path: location.pathname,
        });
      }
    };

    // Add link click tracking
    const handleLinkClick = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === "A" || target.closest("a")) {
        const link = target.tagName === "A" ? target : target.closest("a");
        const linkText = link?.textContent?.trim() || "Unknown Link";
        const linkHref = (link as HTMLAnchorElement)?.href || "";

        trackEvent("link_click", {
          link_text: linkText,
          link_url: linkHref,
          page_path: location.pathname,
        });
      }
    };

    // Add form submission tracking
    const handleFormSubmit = (event: Event) => {
      const target = event.target as HTMLFormElement;
      if (target.tagName === "FORM") {
        const formId = target.id || "unnamed_form";
        const formClass = target.className || "";

        trackEvent("form_submit", {
          form_id: formId,
          form_class: formClass,
          page_path: location.pathname,
        });
      }
    };

    // Add modal/drawer tracking
    const handleModalOpen = (event: Event) => {
      const target = event.target as HTMLElement;
      if (
        target.classList.contains("modal") ||
        target.classList.contains("drawer") ||
        target.classList.contains("manual-drawer-overlay")
      ) {
        trackEvent("modal_open", {
          modal_class: target.className,
          page_path: location.pathname,
        });
      }
    };

    // Attach event listeners
    document.addEventListener("click", handleButtonClick);
    document.addEventListener("click", handleLinkClick);
    document.addEventListener("focus", handleInputFocus, true);
    document.addEventListener("submit", handleFormSubmit);
    document.addEventListener("click", handleModalOpen);

    // Cleanup event listeners
    return () => {
      document.removeEventListener("click", handleButtonClick);
      document.removeEventListener("click", handleLinkClick);
      document.removeEventListener("focus", handleInputFocus, true);
      document.removeEventListener("submit", handleFormSubmit);
      document.removeEventListener("click", handleModalOpen);
    };
  }, [location.pathname]);

  return (
    <>
      <Header />
      <div className="flex-grow-1 d-flex flex-column">{children}</div>
      <Footer />
    </>
  );
};

// Export GTM tracking helper for use in other components
export const trackEvent = (
  eventName: string,
  parameters?: Record<string, any>,
) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...parameters,
    });
  }
};

// Enhanced tracking functions for specific app events
export const trackBadgeScan = (badgeId: string, scanType: "qr" | "manual") => {
  trackEvent("badge_scan", {
    badge_id: badgeId,
    scan_type: scanType,
    timestamp: new Date().toISOString(),
  });
};

export const trackConnection = (
  connectionId: string,
  userBadge: string,
  scannedBadge: string,
) => {
  trackEvent("connection_created", {
    connection_id: connectionId,
    user_badge: userBadge,
    scanned_badge: scannedBadge,
    timestamp: new Date().toISOString(),
  });
};

export const trackNotesSave = (connectionId: string, notesLength: number) => {
  trackEvent("notes_saved", {
    connection_id: connectionId,
    notes_length: notesLength,
    timestamp: new Date().toISOString(),
  });
};

export const trackError = (
  errorType: string,
  errorMessage: string,
  context?: Record<string, any>,
) => {
  trackEvent("error_occurred", {
    error_type: errorType,
    error_message: errorMessage,
    timestamp: new Date().toISOString(),
    ...context,
  });
};

// Helper function to build Digital Backpack URL with UTM parameters
export const buildDigitalBackpackUrl = (
  baseUrl: string,
  eventCode: string,
  badgeId?: string,
  lastName?: string,
): string => {
  const utmShowCode = (eventCode || "UNKNOWN").toUpperCase();

  let url: URL;
  if (eventCode && badgeId && lastName) {
    // SSO URL: {baseUrl}/digitalbackpack/auth/sso/{eventCode}/{badgeId}/{lastName}/4
    const encodedEventCode = encodeURIComponent(eventCode);
    const encodedBadgeId = encodeURIComponent(badgeId);
    const encodedLastName = encodeURIComponent(lastName);
    url = new URL(
      `${baseUrl}/digitalbackpack/auth/sso/${encodedEventCode}/${encodedBadgeId}/${encodedLastName}/4`,
    );
  } else {
    // Fallback login URL: {baseUrl}/digitalbackpack/login/{eventCode}/4
    const encodedEventCode = encodeURIComponent(eventCode || "");
    url = new URL(`${baseUrl}/digitalbackpack/login/${encodedEventCode}/4`);
  }

  url.searchParams.set("utm_source", `attendee_connect_${utmShowCode}`);
  url.searchParams.set("utm_medium", "referral");
  return url.toString();
};

export default PageShell;
